
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Spinner from "../../components/Spinner";

const MarFolder = () => {
  const navigate = useNavigate();
  const { parentId = null } = useParams(); // 支持父文件夹ID参数
  const [userRole, setUserRole] = useState("");
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [navigating, setNavigating] = useState(false);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadAuthor, setUploadAuthor] = useState("");
  const [uploadMessage, setUploadMessage] = useState("");
  const [uploadError, setUploadError] = useState("");

  // Edit subfolders modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState("");
  const [editError, setEditError] = useState("");

  const project = JSON.parse(localStorage.getItem("selectedProject") || "null");
  const projectId = project ? project.id : null;

  const fetchFolders = useCallback(async () => {
    try {
      setLoading(true);
      const url = parentId 
        ? `/api/folders?module=marketing&projectId=${projectId}&parent_id=${parentId}`
        : `/api/folders?module=marketing&projectId=${projectId}&level=1`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch folders");
      }
      const data = await response.json();

      // 处理不同的API响应格式
      let foldersArray = [];
      if (Array.isArray(data)) {
        foldersArray = data;
      } else if (data.folders && Array.isArray(data.folders)) {
        foldersArray = data.folders;
      } else {
        console.warn("Unexpected API response format:", data);
        foldersArray = [];
      }

      setFolders(foldersArray);

      // 如果有parentId，获取当前文件夹信息
      if (parentId) {
        const folderResponse = await fetch(`/api/folders/${parentId}`);
        if (folderResponse.ok) {
          const folderData = await folderResponse.json();
          setCurrentFolder(folderData);
        }
      } else {
        setCurrentFolder(null);
      }
    } catch (err) {
      console.error("Failed to fetch folders:", err);
      setError(err.message);
      setFolders([]); // 确保即使出错也设置为空数组
    } finally {
      setLoading(false);
    }
  }, [projectId, parentId]);

  const fetchBreadcrumbs = async () => {
    if (!parentId) {
      setBreadcrumbs([]);
      return;
    }

    try {
      const response = await fetch(`/api/folders/${parentId}/breadcrumbs`);
      if (response.ok) {
        const data = await response.json();
        setBreadcrumbs(data);
      }
    } catch (err) {
      console.error("Failed to fetch breadcrumbs:", err);
    }
  };

  useEffect(() => {
    const role = localStorage.getItem("userRole");
    if (!role || !projectId) {
      navigate("/");
    } else {
      setUserRole(role);
      fetchFolders();
      fetchBreadcrumbs();
    }

    // 重置navigating状态
    setNavigating(false);
  }, [navigate, projectId, parentId, fetchFolders]);

  const handleFolderClick = (folder) => {
    console.log('Folder clicked:', folder);
    console.log('Folder level:', folder.level);
    console.log('Folder ID:', folder._id);

    setNavigating(true);

    // 统一使用混合视图，所有文件夹都导航到文件列表页面
    console.log(`Navigating to mixed view: /marketingfile_list/${folder._id}`);
    navigate(`/marketingfile_list/${folder._id}`);
  };

  // Edit subfolders functions
  const handleAddSubfolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;

    try {
      const response = await fetch("/api/folders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newFolderName.trim(),
          module: "marketing",
          projectId: projectId,
          parent_id: parentId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create folder");
      }

      setNewFolderName("");
      fetchFolders(); // Refresh folders
      setEditError("");
    } catch (err) {
      console.error("Add folder error:", err);
      setEditError(err.message || "Failed to create folder");
    }
  };

  const handleDeleteSubfolder = async (subfolderId) => {
    if (!window.confirm("Are you sure you want to delete this subfolder and all its contents?")) return;

    try {
      const response = await fetch(`/api/folders/${subfolderId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete folder");
      }
      fetchFolders();
      setEditError("");
    } catch (err) {
      console.error("Delete folder error:", err);
      setEditError(err.message || "Failed to delete folder");
    }
  };

  const handleUpdateSubfolder = async (subfolderId) => {
    if (!editingFolderName.trim()) return;
    try {
      const response = await fetch(`/api/folders/${subfolderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          name: editingFolderName.trim(),
          projectId: projectId,
          module: "marketing" 
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update folder");
      }
      setEditingFolderId(null);
      setEditingFolderName("");
      fetchFolders();
      setEditError("");
    } catch (err) {
      console.error("Update folder error:", err);
      setEditError(err.message || "Failed to update folder");
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploadMessage("");
    setUploadError("");

    if (!uploadFile || !uploadAuthor.trim()) {
      setUploadError("Please select a file and enter the author name.");
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("folderId", parentId || "root"); // 使用当前文件夹ID，如果是根目录则用"root"
    formData.append("projectId", projectId);
    formData.append("author", uploadAuthor);
    formData.append("user_id", localStorage.getItem("userId"));

    try {
      const response = await fetch("/api/upload/marketing", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Upload failed");
      }

      await response.json();
      setUploadMessage("File uploaded successfully!");
      setUploadFile(null);
      setUploadAuthor("");

      setTimeout(() => {
        setShowUploadModal(false);
        setUploadMessage("");
        fetchFolders();
      }, 2000);
    } catch (err) {
      console.error("Upload error:", err);
      setUploadError(err.message || "Failed to upload file");
    }
  };

  const FolderItem = ({ folder }) => {
    return (
      <div className="w-48 h-40 p-2 flex flex-col items-center relative">
        <div
          className="cursor-pointer flex flex-col items-center"
          onClick={() => handleFolderClick(folder)}
        >
          {navigating && (
            <div className="absolute inset-0 bg-white bg-opacity-80 z-10 flex items-center justify-center">
              <Spinner />
            </div>
          )}
          <img
            src={
              (folder.totalFileCount || folder.fileCount) > 0
                ? "/images/folder-withfile.png"
                : "/images/folder-empty.png"
            }
            alt="folder"
            className="w-12 h-12"
          />
          <p className="text-center text-sm font-medium text-[10px] break-words">
            {folder.name}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen">
      <Sidebar userRole={userRole} projectId={projectId} scenario="marketing" />

      <div className="flex-1 p-6 bg-gray-50 text-[12px] ml-[60px]">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-[18px]">
            Marketing Folder: {project ? project.name : 'Unknown'}
            {currentFolder && ` - ${currentFolder.name}`}
          </h2>
          <img src="/images/gemterra-logo-20260112-t.png" alt="Gemterra" className="h-6" />
        </div>

        {/* 面包屑导航 */}
        <nav className="mb-4">
          <ol className="flex items-center space-x-2 text-sm text-gray-600">
            <li>
              <button
                onClick={() => {
                  setNavigating(true);
                  navigate("/marketing_folder");
                }}
                className="text-blue-600 hover:underline"
                disabled={navigating}
              >
                Root
              </button>
            </li>
            {breadcrumbs.map((breadcrumb, index) => (
              <React.Fragment key={breadcrumb._id}>
                <li className="text-gray-400">/</li>
                <li>
                  <button
                    onClick={() => {
                      setNavigating(true);
                      navigate(`/marketingfile_list/${breadcrumb._id}`);
                    }}
                    className="text-blue-600 hover:underline"
                    disabled={navigating}
                  >
                    {breadcrumb.name}
                  </button>
                </li>
              </React.Fragment>
            ))}
          </ol>
        </nav>

        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setShowUploadModal(true)}
            className="text-white px-3 py-1 rounded text-[12px]"
            style={{ backgroundColor: "#6FA944" }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#5C8E37")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#6FA944")}
          >
            Upload File to {currentFolder ? currentFolder.name : 'Root'}
          </button>
          <button
            onClick={() => setShowEditModal(true)}
            className="text-white px-3 py-1 rounded text-[12px]"
            style={{ backgroundColor: "#6FA944" }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#5C8E37")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#6FA944")}
          >
            Edit Subfolders
          </button>
          {parentId && (
            <button
              onClick={() => {
                setNavigating(true);
                const parentBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
                if (parentBreadcrumb) {
                  navigate(`/marketingfile_list/${parentBreadcrumb._id}`);
                } else {
                  navigate("/marketing_folder");
                }
              }}
              className="bg-gray-300 text-black px-3 py-1 rounded hover:bg-gray-400 text-[12px]"
              disabled={navigating}
            >
              Back to Parent
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div>
            <div className="mb-4 text-sm text-gray-600">
              {currentFolder ? (
                <span>Level {currentFolder.level} - {Array.isArray(folders) ? folders.length : 0} subfolders</span>
              ) : (
                <span>Level 1 Folders - {Array.isArray(folders) ? folders.length : 0} folders</span>
              )}
            </div>
            <div className="flex flex-wrap gap-5">
              {Array.isArray(folders) && folders.length > 0 ? (
                folders.map((folder) => (
                  <FolderItem key={folder._id} folder={folder} />
                ))
              ) : (
                <p className="text-gray-500">No folders found</p>
              )}
            </div>
          </div>
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-96 max-w-[90vw]">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  Upload File to {currentFolder ? currentFolder.name : 'Root'}
                </h3>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadFile(null);
                    setUploadAuthor("");
                    setUploadMessage("");
                    setUploadError("");
                  }}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ×
                </button>
              </div>

              {uploadError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {uploadError}
                </div>
              )}

              {uploadMessage && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                  {uploadMessage}
                </div>
              )}

              <form onSubmit={handleUpload} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select File
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.docx,.xlsx,.jpg,.jpeg,.png,.dwg,.mov,.mp4,.avi,.wmv,.flv,.mkv,.webm,.zip,.rar"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    className="w-full p-2 border rounded text-[12px]"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Allowed: PDF, DOCX, XLSX, JPG, PNG, DWG, video, ZIP, RAR
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Author
                  </label>
                  <input
                    type="text"
                    value={uploadAuthor}
                    onChange={(e) => setUploadAuthor(e.target.value)}
                    className="w-full p-2 border rounded text-[12px]"
                    placeholder="Enter author name"
                    required
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowUploadModal(false);
                      setUploadFile(null);
                      setUploadAuthor("");
                      setUploadMessage("");
                      setUploadError("");
                    }}
                    className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400 text-[12px]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="text-white px-4 py-2 rounded text-[12px]"
                    style={{ backgroundColor: "#6FA944" }}
                    onMouseOver={(e) => (e.target.style.backgroundColor = "#5C8E37")}
                    onMouseOut={(e) => (e.target.style.backgroundColor = "#6FA944")}
                  >
                    Upload File
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Subfolders Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-[600px] max-w-[90vw] max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  Edit Subfolders in {currentFolder ? currentFolder.name : 'Root'}
                </h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setNewFolderName("");
                    setEditingFolderId(null);
                    setEditingFolderName("");
                    setEditError("");
                  }}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ×
                </button>
              </div>

              {editError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {editError}
                </div>
              )}

              {/* Add new subfolder form */}
              <form onSubmit={handleAddSubfolder} className="mb-6 p-4 bg-gray-50 rounded">
                <h4 className="text-md font-medium mb-3">Add New Subfolder</h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Subfolder name"
                    className="flex-1 p-2 border rounded text-[12px]"
                    required
                  />
                  <button
                    type="submit"
                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 text-[12px]"
                  >
                    Add
                  </button>
                </div>
              </form>

              {/* Existing subfolders list */}
              <div>
                <h4 className="text-md font-medium mb-3">Existing Subfolders</h4>
                {folders.length === 0 ? (
                  <p className="text-gray-500">No subfolders found.</p>
                ) : (
                  <div className="space-y-2">
                    {folders.map((folder) => (
                      <div
                        key={folder._id}
                        className="flex items-center justify-between p-3 border rounded hover:bg-gray-50"
                      >
                        <div className="flex items-center space-x-2">
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            Level {folder.level}
                          </span>
                          {editingFolderId === folder._id ? (
                            <input
                              type="text"
                              value={editingFolderName}
                              onChange={(e) => setEditingFolderName(e.target.value)}
                              className="p-1 border rounded text-[12px]"
                              autoFocus
                            />
                          ) : (
                            <span className="font-medium">{folder.name}</span>
                          )}
                          <span className="text-xs text-gray-500">
                            ({folder.fileCount || 0} files)
                          </span>
                        </div>

                        <div className="flex gap-2">
                          {editingFolderId === folder._id ? (
                            <>
                              <button
                                onClick={() => handleUpdateSubfolder(folder._id)}
                                className="bg-green-500 text-white px-2 py-1 rounded text-[12px] hover:bg-green-600"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingFolderId(null);
                                  setEditingFolderName("");
                                }}
                                className="bg-gray-500 text-white px-2 py-1 rounded text-[12px] hover:bg-gray-600"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  setEditingFolderId(folder._id);
                                  setEditingFolderName(folder.name);
                                }}
                                className="bg-blue-500 text-white px-2 py-1 rounded text-[12px] hover:bg-blue-600"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteSubfolder(folder._id)}
                                className="bg-red-500 text-white px-2 py-1 rounded text-[12px] hover:bg-red-600"
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setNewFolderName("");
                    setEditingFolderId(null);
                    setEditingFolderName("");
                    setEditError("");
                  }}
                  className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400 text-[12px]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarFolder;
